import { useState, useEffect, useRef, memo } from 'react';
import { useHistory } from 'react-router-dom';
import { useCooperativeId } from 'hooks';

import services from 'services';
import { ReportId } from 'models';
import _get from 'lodash/get';
import _set from 'lodash/set';

import { HTMLParserView } from 'components';

interface Ref {
  report: HTMLDivElement | null;
  subReport: HTMLDivElement | null;
}

interface Props {
  url: string;
  reportId?: ReportId;
  fiscalYearId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

interface State {
  text: any;
  loading: boolean;
  error: boolean;
  isSub: boolean;
  isSubPending: boolean;
  ledgerAccountId: string | null;
  __html: any;
  __htmlSub: any;
}

const HTMLParser: React.FC<Props> = ({
  url,
  reportId = 'default',
  fiscalYearId,
  startDate,
  endDate,
}) => {
  const coopId = useCooperativeId();
  const reportsRef = useRef<Ref | null>(null);
  const history = useHistory();
  const [state, setState] = useState<State>({
    loading: true,
    error: false,
    text: null,
    isSub: false,
    isSubPending: false,
    ledgerAccountId: null,
    __html: null,
    __htmlSub: null,
  });

  const placeSubReport = () => {
    if (reportsRef.current) {
      const { subReport } = reportsRef.current;

      const getElementCoordinates = (elem: HTMLElement) => {
        const elementRect = elem.getBoundingClientRect(),
          body = document.body,
          docElement = document.documentElement,
          scrollTop =
            window.pageYOffset || docElement.scrollTop || body.scrollTop,
          scrollLeft =
            window.pageXOffset || docElement.scrollLeft || body.scrollLeft,
          clientTop = docElement.clientTop || body.clientTop || 0,
          clientLeft = docElement.clientLeft || body.clientLeft || 0,
          top = elementRect.top + scrollTop - clientTop,
          left = elementRect.left + scrollLeft - clientLeft;

        return { top, left };
      };

      if (subReport && subReport.parentElement) {
        let reportContainerCoord = getElementCoordinates(
            subReport.parentElement
          ),
          parentPaddingRight = parseInt(
            window
              .getComputedStyle(subReport.parentElement, null)
              .getPropertyValue('padding-right')
          ),
          parentPaddingTop = parseInt(
            window
              .getComputedStyle(subReport.parentElement, null)
              .getPropertyValue('padding-top')
          ),
          parentMarginTop = parseInt(
            window
              .getComputedStyle(subReport.parentElement, null)
              .getPropertyValue('margin-top')
          );

        let componentOffsetY =
          reportContainerCoord.top +
          parseFloat(`${parentPaddingTop || 0}`) +
          parseFloat(`${parentMarginTop || 0}`);

        if (componentOffsetY < window.pageYOffset) {
          subReport.style.left =
            reportContainerCoord.left +
            subReport.parentElement.offsetWidth / 2 +
            'px';
          subReport.style.top = '0px';
          subReport.style.position = 'fixed';
        } else {
          subReport.style.position = 'static';
        }

        let subReportRect = subReport.getBoundingClientRect(),
          subReportHeight = window.innerHeight - subReportRect.top,
          subReportWidth =
            window.innerWidth - subReportRect.left - parentPaddingRight;

        subReport.style.height = `${subReportHeight}px`;
        subReport.style.width = `${subReportWidth}px`;
      }
    }
  };

  const parseHTML = (text: any, isSub: boolean, reportId: string) => {
    const parser = new DOMParser(),
      html = parser.parseFromString(text, 'text/html'),
      body = html.getElementsByTagName('body')[0];

    Array.from(body.getElementsByTagName('img')).forEach((img) => {
      img.style.maxWidth = '100%';
      img.style.maxHeight = '100%';
    });

    body.appendChild(html.getElementsByTagName('style')[0]);

    if (['cooperative-budget'].includes(reportId)) {
      const contentTables: HTMLTableElement[] = [];
      const reportTables = Array.from(body.getElementsByTagName('table'));

      for (let reportTable of reportTables) {
        let reportTableRows = reportTable.getElementsByTagName('tr');
        if (
          reportTableRows.length >= 2 &&
          reportTableRows[1].children.length > 1 &&
          reportTableRows[1].children[0].children.length === 1 &&
          reportTableRows[1].children[0].children[0].textContent ===
            'uds ledgeraccountgroupid'
        ) {
          contentTables.push(reportTable);
        }
      }

      if (contentTables.length) {
        for (let i = 0; i < contentTables.length; i++) {
          const contentTableRows = Array.from(
            contentTables[i].getElementsByTagName('tr')
          );
          for (let contentTableRow of contentTableRows) {
            (contentTableRow.children[0] as HTMLElement).style.display = 'none';
          }

          if (contentTables.length > 1 && i > 0) {
            for (let newContentTableRow of Array.from(contentTableRows).slice(
              2
            )) {
              contentTables[0].appendChild(newContentTableRow);
            }
            contentTables[i].style.display = 'none';
          }
        }

        const mainTableRows = Array.from(
          contentTables[0].getElementsByTagName('tr')
        ).slice(2);

        console.log(mainTableRows);

        for (let contentRow of mainTableRows) {
          if (contentRow.children[0].children.length) {
            let ledgerAccountId =
              contentRow.children[0].children[0].textContent;
            let linkElem = contentRow.children[1].children[0].children[0];

            if (ledgerAccountId && linkElem) {
              linkElem.setAttribute('data-ledgeraccountid', ledgerAccountId);
              linkElem.setAttribute(
                'data-reportid',
                'cooperative-budget-ledger'
              );
            }
          }
        }
      }

      const lines = Array.from(body.getElementsByTagName('hr'));
      for (let line of lines) {
        line.style.display = 'none';
      }

      for (let reportElement of Array.from(body.children)) {
        if (
          reportElement.tagName === 'DIV' &&
          reportElement.id !== 'oReportDiv'
        ) {
          (reportElement as HTMLElement).style.display = 'none';
        }
      }
    }

    if (['cooperative-budget-ledger'].includes(reportId)) {
      const contentTables: Element[] = [];
      const reportTables = Array.from(body.querySelectorAll('table[cols]'));

      for (let reportTable of reportTables) {
        let reportTableRows = reportTable.getElementsByTagName('tr');
        if (
          reportTableRows.length >= 2 &&
          reportTableRows[1].children.length === 6
        ) {
          contentTables.push(reportTable);
        }
      }

      if (contentTables.length) {
        for (let i = 0; i < contentTables.length; i++) {
          const contentTableRows = Array.from(
            contentTables[i].getElementsByTagName('tr')
          );
          for (let contentTableRow of contentTableRows) {
            (contentTableRow.children[0] as HTMLElement).style.display = 'none';
          }

          if (contentTables.length > 1 && i > 0) {
            for (let newContentTableRow of Array.from(contentTableRows).slice(
              2
            )) {
              contentTables[0].appendChild(newContentTableRow);
            }
            (contentTables[i] as HTMLElement).style.display = 'none';
          }
        }

        const mainTableContentRows = Array.from(
          contentTables[0].getElementsByTagName('tr')
        ).slice(2);

        for (let contentRow of mainTableContentRows) {
          (contentRow.children[0] as HTMLElement).style.display = 'none';

          if (contentRow.children.length) {
            let invoiceId =
              contentRow.children[0].children &&
              contentRow.children[0].children.length
                ? contentRow.children[0].children[0].textContent
                : null;
            let invoiceName = contentRow.children[3].textContent;

            // if (invoiceName === "Aulis Mutikainen") {
            //   var a = "dsds";
            // }

            if (invoiceId && invoiceId.trim() && invoiceName) {
              const nameElement = contentRow.children[3];
              nameElement.removeChild(nameElement.children[0]);

              var linkElem = document.createElement('a');
              var linkText = document.createTextNode(invoiceName);
              linkElem.appendChild(linkText);
              linkElem.title = invoiceName;
              linkElem.setAttribute('data-invoiceid', invoiceId);
              linkElem.setAttribute('data-pageid', 'invoice');

              nameElement.appendChild(linkElem);
            }
          }
        }
      }

      const lines = Array.from(body.getElementsByTagName('hr'));
      for (let line of lines) {
        line.style.display = 'none';
      }

      for (let reportElement of Array.from(body.children)) {
        if (
          reportElement.tagName === 'DIV' &&
          reportElement.id === 'oReportDiv'
        ) {
          (reportElement as HTMLElement).style.height = '';
          (reportElement.children[0] as HTMLElement).style.height = '';
        }
      }
    }

    if (['cooperative'].includes(reportId)) {
      const lines = Array.from(body.getElementsByTagName('hr'));
      for (let line of lines) {
        line.style.display = 'none';
      }
      let signature = Array.from(body.getElementsByTagName('img'))[1];
      if (signature) {
        // signature.src = this.PropertyManager ? (this.PropertyManager.WrittenSignatureDataUrl ? this.PropertyManager.WrittenSignatureDataUrl : "") : "";
      }
    }

    if (isSub) {
      setState((prevState) => ({
        ...prevState,
        isSubPending: false,
        __htmlSub: body.innerHTML,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        loading: false,
        __html: body.innerHTML,
        __htmlSub: null,
      }));
    }
  };

  useEffect(() => {
    const fn = () => placeSubReport();
    window.addEventListener('resize', fn);
    window.addEventListener('scroll', fn);

    return () => {
      window.removeEventListener('resize', fn);
      window.removeEventListener('scroll', fn);
    };
  }, []);

  useEffect(() => {
    (async function () {
      try {
        setState((prevState) => ({
          ...prevState,
          loading: true,
          error: false,
        }));

        const text = (await services.get(url)).data;

        parseHTML(text, state.isSub, reportId);

        setState((prevState) => ({ ...prevState, loading: false }));
      } catch (err) {
        console.error(err);
        setState((prevState) => ({
          ...prevState,
          error: true,
          loading: false,
        }));
      }
    })();
  }, [url]);

  const requestSubReport = async (
    ledgerAccountId: string | null,
    reportId: string
  ) => {
    let uri = `/reports/cooperative/budget/${coopId}/ledger?ledgerAccountId=${ledgerAccountId}&fiscalYearId=${fiscalYearId}&startDate=${startDate}&endDate=${endDate}`;

    setState((prevState) => ({ ...prevState, isSubPending: true }));

    try {
      const text = (await services.get(uri)).data;

      parseHTML(text, true, reportId);

      setState((prevState) => ({
        ...prevState,
        isSubPending: false,
      }));
    } catch (err) {
      console.error(err);
      setState((prevState) => ({
        ...prevState,
        isSubPending: false,
        error: true,
      }));
    }
  };

  useEffect(() => {
    if (reportsRef.current) {
      const { report, subReport } = reportsRef.current;

      if (report) {
        let subReportLinks =
          report.querySelectorAll<HTMLElement>('[data-reportid]');
        let subPagesLinks = subReport
          ? subReport.querySelectorAll<HTMLElement>('[data-pageid]')
          : null;

        subReportLinks.forEach((subReportLink) => {
          let subReportId = subReportLink.getAttribute('data-reportid');

          if (
            subReportId &&
            ['cooperative-budget-ledger'].includes(subReportId)
          ) {
            let ledgerAccountId = subReportLink.getAttribute(
              'data-ledgeraccountid'
            );

            if (!_get(subReportLink, 'subReportLinkAdded')) {
              subReportLink.addEventListener(
                'click',
                function () {
                  requestSubReport(
                    ledgerAccountId,
                    'cooperative-budget-ledger'
                  );
                },
                false
              );

              subReportLink &&
                subReportLink.addEventListener(
                  'mouseover',
                  function () {
                    subReportLink && (subReportLink.style.cursor = 'pointer');
                  },
                  false
                );
              _set(subReportLink, 'subReportLinkAdded', true);
            }
          }

          if (subPagesLinks) {
            subPagesLinks.forEach((subPageLink) => {
              let subPageId = subPageLink.getAttribute('data-pageid');

              if (subPageId && ['invoice'].includes(subPageId)) {
                let invoiceId = subPageLink.getAttribute('data-invoiceid');

                if (!_get(subPageLink, 'subPageLinkAdded') && invoiceId) {
                  subPageLink.style.cursor = 'pointer';
                  subPageLink.style.textDecoration = 'underline';

                  subPageLink.addEventListener(
                    'click',
                    function () {
                      history.push(`/incoming-invoices/${invoiceId}`);
                    },
                    false
                  );
                  subPageLink.addEventListener(
                    'mouseover',
                    function () {
                      subReportLink && (subReportLink.style.cursor = 'pointer');
                    },
                    false
                  );
                  _set(subPageLink, 'subPageLinkAdded', true);
                }
              }
            });
          }
        });
      }
    }

    placeSubReport();
  });

  return (
    <HTMLParserView
      loading={state.loading}
      error={state.error}
      ref={reportsRef}
      __html={state.__html}
      __htmlSub={state.__htmlSub}
      subLoading={state.isSubPending}
      withSub={state.isSub}
    />
  );
};

export default memo(HTMLParser);
